/**
 * Medição/animação da altura da caixa e do chip da ferramenta no `send-message`.
 *
 * Extraído do componente porque `send-message.vue` passava do teto de 500
 * linhas do repo (achado do review de design-system do PR #2041) — esta é a
 * fatia de estado/lógica que só existe para o campo trocar entre fileira
 * única e dupla e animar a transição, sem nenhuma relação com o resto do
 * composer (anexos, catálogo de comandos, envio).
 *
 * Contrato do consumidor: precisa de `$refs.caixa` (a caixa que anima),
 * `$refs.campo` (o `input-by-type` que envolve o `<textarea>`) e
 * `$refs.chipDaFerramenta` (o `ComandoChip` flutuante, `v-if`) e do computed
 * `chipDoComposer` (mixin de atalhos), e deve chamar
 * `this.medirAltura()` no próprio `onInput`. `classesCaixa` (que fica no
 * host) lê `this.multiLinha`/`this.animandoAltura` daqui.
 */

/**
 * Acima desta altura o campo deixou de caber em UMA linha, e o composer troca
 * para duas fileiras (campo em cima, controles embaixo).
 *
 * 40px: a linha é 14px de fonte com o line-height do campo (~20px). O limiar
 * fica acima de uma linha e abaixo de duas, com folga para o arredondamento do
 * `scrollHeight` — comparar com o valor exato de uma linha alternaria o layout
 * por causa de meio pixel.
 */
const LIMIAR_DE_MULTILINHA = 40
// Respiro entre o chip da ferramenta e o começo do texto, quando ele flutua
// sobre a primeira linha — mesma folga que o `gap` da caixa dá entre irmãos.
const ESPACO_APOS_O_CHIP = 6
// A classe do modo largo. Aplicada por um instante para medir — ver
// `alturaNaFileiraUnica`.
const CLASSE_MULTILINHA = 'send-message-inner--multilinha'
// Abertura e fechamento da caixa — ver `animarAlturaDaCaixa`.
const DURACAO_DA_ABERTURA = 160
// Folga da rede de segurança que desliga o recorte — ver `animarAlturaDaCaixa`.
const FOLGA_DA_REDE = 80
const CURVA_DA_ABERTURA = 'cubic-bezier(0.2, 0, 0, 1)'

export const sendMessageAlturaMixin = {
  data() {
    return {
      // Largura do chip da ferramenta, medida no DOM — ver `recuoDaPrimeiraLinha`.
      larguraDoChip: 0,
      observadorDoChip: null,
      // Campo passou de uma linha — ver `medirAltura`. Só o modo limpo usa.
      multiLinha: false,
      // Quanto o texto já rolou dentro do campo — ver `estiloDoChip`.
      rolagemDoCampo: 0,
      // Onde começa a primeira linha do texto, medido — ver `medirTopoDaLinha`.
      topoDaPrimeiraLinha: 0,
      // Altura da caixa ANTES da tecla atual — ver `animarAlturaDaCaixa`.
      alturaDaCaixaAntes: 0,
      // Caixa abrindo ou fechando agora — ver `animarAlturaDaCaixa`.
      animandoAltura: false,
    }
  },
  computed: {
    /**
     * O quanto a PRIMEIRA linha do texto recua para caber o chip ao lado.
     *
     * ★ É o que faz o chip parecer parte do texto, como no ChatGPT. Lá o campo
     * é um `contenteditable` e o chip é um nó no meio do conteúdo; aqui é um
     * `<textarea>`, que só aceita texto — então o chip flutua por cima e o
     * `text-indent` abre o buraco exato para ele. `text-indent` afeta SÓ a
     * primeira linha, que é justamente o comportamento da referência: as
     * seguintes voltam à margem.
     *
     * ⚠️ Medido em runtime, e não fixo: a largura muda com o rótulo ("CRM" tem
     * 3 letras, "Financial Planning" tem 18) e com a fonte. Um valor chutado
     * deixaria o texto invadindo o chip num módulo e um buraco em outro.
     *
     * ⚠️ NÃO depende de `multiLinha`: quem restringe o recuo à fileira dupla é
     * o Sass (a regra vive dentro do bloco `--multilinha`), então repetir a
     * condição aqui seria a mesma regra escrita em dois lugares — e a medição
     * de `alturaNaFileiraUnica`, que TIRA aquela classe, já fica sem o recuo
     * por consequência.
     */
    recuoDaPrimeiraLinha() {
      if (!this.chipDoComposer) return '0px'
      if (!this.larguraDoChip) return '0px'
      return this.larguraDoChip + ESPACO_APOS_O_CHIP + 'px'
    },
    // Custom property na caixa em vez de `style` no campo: o `input-by-type`
    // é quem recebe atributos soltos, e o `<textarea>` está dois níveis abaixo
    // dele. A variável desce pelo CSS e a regra profunda a consome.
    /**
     * O chip acompanha a rolagem do texto.
     *
     * ⚠️ Ele é `position: absolute` porque não existe outro jeito: o campo é um
     * `<textarea>`, que só renderiza texto puro — o chip é irmão dele, e o que
     * o faz PARECER inline é o `text-indent` da primeira linha. O preço é que
     * ele fica preso à caixa: quando o texto passa do teto e começa a rolar
     * DENTRO do campo, a primeira linha some por baixo e o chip continua lá,
     * sobreposto ao texto que rolou.
     *
     * Empurrá-lo pelo `scrollTop` devolve o comportamento de texto: ele sobe
     * junto com a linha a que pertence e sai de cena com ela — quem o recorta é
     * o `overflow` da caixa. Voltando ao topo, ele volta.
     *
     * Só na fileira dupla: na única o chip é item de flex normal, e transladar
     * o tiraria do lugar.
     */
    estiloDoChip() {
      if (!this.multiLinha || !this.rolagemDoCampo) return null
      // Só a MEDIDA — o deslocamento e o corte são desenhados no Sass, ao lado
      // do `top` de onde os dois números saem. JS informa o que o CSS não tem
      // como saber (a rolagem); o resto é layout e fica no layout.
      return { '--rolagem': this.rolagemDoCampo + 'px' }
    },
    estiloDaCaixa() {
      return {
        '--recuo-do-chip': this.recuoDaPrimeiraLinha,
        '--topo-da-linha': this.topoDaPrimeiraLinha + 'px',
      }
    },
  },
  watch: {
    // O chip é `v-if`: trocar de módulo DESTRÓI e recria o elemento, e o
    // observador ficaria apontando para um nó fora do documento. Remedir só
    // não bastaria — o `ResizeObserver` precisa ser reapontado.
    chipDoComposer() {
      this.$nextTick(() => {
        this.medirOChip()
        this.observarElementoDoChip()
        // Ligar/desligar a ferramenta muda a largura do campo na fileira única
        // — a régua e a decisão precisam ser refeitas mesmo sem tecla nova.
        this.medirAltura()
      })
    },
    // Entrar em duas fileiras é quando o recuo passa a valer; o chip pode já
    // existir há vários caracteres.
    multiLinha() {
      this.$nextTick(() => {
        // ⚠️ Ajustar ANTES de medir: o wrapper do campo centraliza o textarea,
        // então mudar a altura dele move o topo da primeira linha. Medindo
        // primeiro, o chip era ancorado num layout que ainda ia mudar — 6px de
        // desvio no instante exato da troca, medido em 28/08.
        this.ajustarAlturaDoCampo()
        this.medirOChip()
      })
    },
  },
  mounted() {
    // ⚠️ Fase de CAPTURA: `scroll` não borbulha, então um listener na caixa
    // nunca veria a rolagem que acontece dentro do campo.
    this.$el.addEventListener('scroll', this.aoRolarOCampo, true)
    this.observarOChip()
  },
  beforeDestroy() {
    this.$el.removeEventListener('scroll', this.aoRolarOCampo, true)
    if (this.observadorDoChip) this.observadorDoChip.disconnect()
  },
  methods: {
    // O `<textarea>` de verdade, dentro do wrapper do `input-by-type` e do slot
    // do Vuetify. Um lugar só — `focarCampo` e `medirAltura` procuram o mesmo.
    elementoDoCampo() {
      const campo = this.$refs.campo
      return (campo && campo.$el && campo.$el.querySelector('textarea')) || null
    },
    /**
     * Passou de uma linha? Então o composer troca para DUAS FILEIRAS.
     *
     * ⚠️ Precisa ser medido, e não deduzido do texto: `\n` só cobre a quebra
     * manual, e a linha também estoura por WRAP quando a frase é longa. Quem
     * sabe as duas coisas é o `scrollHeight` do elemento.
     *
     * Em `$nextTick` porque o `auto-grow` do Vuetify escreve a altura inline
     * DEPOIS do input — medir antes leria sempre a altura anterior.
     */
    aoRolarOCampo(evento) {
      const alvo = evento.target
      if (alvo !== this.elementoDoCampo()) return
      this.rolagemDoCampo = alvo.scrollTop
    },
    medirAltura() {
      this.$nextTick(() => {
        const textarea = this.elementoDoCampo()
        if (!textarea) return
        // Apagar texto encolhe o campo sem emitir `scroll` quando ele deixa de
        // rolar — sem esta linha o chip ficaria deslocado para cima.
        this.rolagemDoCampo = textarea.scrollTop
        this.multiLinha =
          this.alturaNaFileiraUnica(textarea) > LIMIAR_DE_MULTILINHA
        // ⚠️ `$nextTick` aninhado: o watcher de `multiLinha` registra o dele
        // ANTES deste, então quando este roda a troca de fileira e o
        // `ajustarAlturaDoCampo` já aconteceram e a altura final é a de agora.
        this.$nextTick(() => {
          // O topo da linha muda com a altura do campo (o wrapper centraliza),
          // então acompanha cada tecla, não só a troca de fileira.
          this.medirTopoDaLinha()
          this.animarAlturaDaCaixa()
        })
      })
    },
    /**
     * Altura que o texto teria na FILEIRA ÚNICA — a pergunta que decide o modo.
     *
     * ★ A caixa abre assim que o texto chega ao fim da linha, como no ChatGPT
     * (decisão de produto, 28/08). A alternativa — só abrir quando não coubesse
     * na largura cheia — criava uma faixa de ~15 caracteres em que o texto já
     * tinha quebrado em duas linhas mas a caixa continuava compacta, e era esse
     * o vai-e-vem. Aqui a fileira única, por construção, nunca tem duas linhas.
     *
     * ⚠️ Medir no campo como ele está agora fecharia um ciclo: o modo muda o
     * campo, o campo muda onde o texto quebra, e a quebra decide o modo de
     * novo. Com uma palavra longa e sem espaço — `ddddd…` — cada tecla desfazia
     * a decisão da anterior: 69 largo, 70 estreito, 71 largo.
     *
     * ★ Por isso a medição TIRA a classe do modo largo em vez de imitar o modo
     * estreito. São quatro coisas que mudam juntas — largura (750 → 528),
     * `text-indent` (o do chip → 0), padding (7/7 → 0/12) e o `flex` — e
     * simular uma só cai numa quinta combinação, que não é nenhum dos modos.
     * O `flex` foi o que sobreviveu a três tentativas: o `.v-text-field__slot`
     * é `display: flex` com o campo em `flex: 1 1 auto`, então `width: 528px`
     * era simplesmente ignorado (medido em 28/08 — pedindo 528 ele continuava
     * com 750). Com a classe, os quatro vêm do CSS e nenhum número vive
     * duplicado aqui.
     *
     * ⚠️ Zerar a altura antes de ler não é opcional: `scrollHeight` nunca é
     * menor que a caixa, então numa caixa de 51px um texto que voltou a caber
     * em 33 responderia 51 e ele nunca sairia do modo largo. Mesma manobra do
     * `calculateInputHeight` do Vuetify; depende de NÃO haver transição em
     * `height` no campo (verificado: `all 0s`) — foi por isso que a animação de
     * crescimento saiu daqui.
     *
     * Tudo é escrito e desfeito no mesmo passo, sem quadro pintado no meio.
     */
    alturaNaFileiraUnica(textarea) {
      const caixa = this.$refs.caixa
      if (!caixa) return textarea.scrollHeight
      const estavaLarga = caixa.classList.contains(CLASSE_MULTILINHA)
      const alturaAnterior = textarea.style.height
      if (estavaLarga) caixa.classList.remove(CLASSE_MULTILINHA)
      textarea.style.height = '0'
      const altura = textarea.scrollHeight
      textarea.style.height = alturaAnterior
      if (estavaLarga) caixa.classList.add(CLASSE_MULTILINHA)
      return altura
    },
    /**
     * Reescreve a altura do campo para o conteúdo na largura ATUAL.
     *
     * ⚠️ O `auto-grow` do Vuetify só recalcula quando o VALOR muda
     * (`VTextarea` observa `value` e `rowHeight`), e a troca de modo muda a
     * LARGURA sem tocar no texto. Sem isto o campo carrega a altura calculada
     * para a largura anterior até a próxima tecla: ao abrir, a caixa ficava com
     * 51px para um texto que agora cabe em 33 — uma linha vazia sobrando, que é
     * exatamente o "desceu mais do que devia".
     *
     * Mesma manobra do `calculateInputHeight` do Vuetify, e pelo mesmo motivo o
     * campo não pode ter transição em `height`.
     */
    ajustarAlturaDoCampo() {
      const textarea = this.elementoDoCampo()
      if (!textarea) return
      textarea.style.height = '0'
      textarea.style.height = textarea.scrollHeight + 'px'
    },
    /**
     * Mede o chip e mantém a medida em dia.
     *
     * ⚠️ `ResizeObserver`, e não uma medição única: a largura muda ao TROCAR de
     * módulo (o `v-if` remonta o elemento), ao a fonte carregar e ao a janela
     * mudar de tamanho. Sem ele, escolher "Financial Planning" depois de "CRM"
     * deixaria o texto por cima do chip — o recuo continuaria o do rótulo curto.
     *
     * Guardado atrás de `typeof`: o `ResizeObserver` não existe no jsdom dos
     * testes, e sem a guarda o `mounted` derrubaria toda montagem do composer.
     */
    observarOChip() {
      this.medirOChip()
      if (typeof ResizeObserver === 'undefined') return
      this.observadorDoChip = new ResizeObserver(() => this.medirOChip())
      this.observarElementoDoChip()
    },
    observarElementoDoChip() {
      if (!this.observadorDoChip) return
      this.observadorDoChip.disconnect()
      const chip = this.elementoDoChip()
      if (chip) this.observadorDoChip.observe(chip)
    },
    // O chip é o `ComandoChip`: o ref devolve a instância, e quem mede é o `$el`.
    elementoDoChip() {
      const chip = this.$refs.chipDaFerramenta
      if (!chip) return null
      return chip.$el || chip
    },
    medirOChip() {
      const chip = this.elementoDoChip()
      this.larguraDoChip = chip ? chip.offsetWidth : 0
      this.medirTopoDaLinha()
    },
    /**
     * Onde a primeira linha do texto começa, em relação ao topo da caixa.
     *
     * ⚠️ Medido, não escrito. Este número já foi a soma `8 + 7 + 7 = 22px` no
     * Sass e envelheceu duas vezes: quem o compõe é o padding da caixa, o
     * wrapper do `input-by-type`, o slot do Vuetify e o `padding-top` do campo
     * — quatro fontes, e qualquer ajuste em uma delas desalinhava o chip em
     * silêncio. Em 28/08 o topo real do campo era 23, não 15, e o chip ficava
     * 7px acima da linha.
     *
     * Só faz sentido na fileira dupla, que é onde o chip é absoluto.
     */
    medirTopoDaLinha() {
      const textarea = this.elementoDoCampo()
      const caixa = this.$refs.caixa
      if (!textarea || !caixa) return
      const recuoInterno =
        parseFloat(window.getComputedStyle(textarea).paddingTop) || 0
      const topo =
        textarea.getBoundingClientRect().top - caixa.getBoundingClientRect().top
      this.topoDaPrimeiraLinha = Math.round(topo + recuoInterno)
    },
    /**
     * A caixa abre e fecha animada.
     *
     * ⚠️ Quem anima é a CAIXA, nunca o `<textarea>`: o Vuetify mede a altura do
     * campo zerando-a e lendo o `scrollHeight`, e uma transição ali impede que
     * ela chegue a zero — o campo passa a só crescer, e apagar o texto não o
     * encolhe mais (medido em 28/08: 33 → 87 → 87 com transição, 33 → 87 → 33
     * sem). A altura da caixa não entra em nenhuma medição, então é seguro.
     *
     * Web Animations em vez de `transition`: a altura de uma caixa que cresce
     * com o conteúdo é `auto`, e `auto` não interpola — não há transição de CSS
     * possível aqui, com ou sem `interpolate-size`. E a WAAPI não deixa rastro
     * no `style`, então não há altura fixa para limpar se algo interromper.
     *
     * ⚠️ O `overflow` vale só durante a animação: o menu de ferramentas é filho
     * da caixa e seria recortado se ficasse (aviso registrado no próprio
     * `.send-message-inner`). Sem ele, a fileira de baixo apareceria fora da
     * borda enquanto ela cresce.
     */
    animarAlturaDaCaixa() {
      const caixa = this.$refs.caixa
      const de = this.alturaDaCaixaAntes
      if (!caixa || !de || typeof caixa.animate !== 'function') return
      if (this.movimentoReduzido()) return
      const para = caixa.offsetHeight
      if (de === para) return
      caixa.getAnimations().forEach((animacao) => animacao.cancel())
      // ⚠️ Estado, e não `classList.add`: o `class` desta caixa é ligado pelo
      // Vue (`classesCaixa`), e cada re-render reescreve o atributo inteiro —
      // uma classe posta na mão sobrevive até a próxima tecla e some. Medido em
      // 28/08: o recorte não estava valendo durante a animação.
      this.animandoAltura = true
      const animacao = caixa.animate(
        [{ height: de + 'px' }, { height: para + 'px' }],
        { duration: DURACAO_DA_ABERTURA, easing: CURVA_DA_ABERTURA }
      )
      const encerrar = () => {
        this.animandoAltura = false
      }
      animacao.onfinish = encerrar
      animacao.oncancel = encerrar
      // ⚠️ Rede de segurança, e não zelo excessivo: a linha do tempo da WAAPI
      // PARA quando a aba não está sendo desenhada, e aí nem `onfinish` nem
      // `oncancel` disparam — medido em 28/08, meio segundo depois a marca
      // ainda estava ligada. Como ela é quem liga o `overflow`, ficar presa
      // significaria recortar o menu de ferramentas, que é filho da caixa.
      // Timer dispara mesmo com a aba oculta; a animação parada não é vista.
      setTimeout(encerrar, DURACAO_DA_ABERTURA + FOLGA_DA_REDE)
    },
    movimentoReduzido() {
      if (typeof window.matchMedia !== 'function') return false
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
  },
}
